import { useState } from "react";
import { connect } from "react-redux";
import { bool, func, number, shape, string } from "prop-types";
import Modal from "@shared/v2/Modal/Modal";
import Button from "@shared/v2/Button/Button";
import { Spinner } from "@shared/v2/Icomoon";
import Alert from "@shared/v2/Alert/Alert";
import { uploadDocumentAsThunk } from "../../../Main/Docs/actions/thunks";
import { submitBlueprintAsThunk } from "../actions/thunks";
import { mapToContactPayload } from "../model/ConactOptions";
import { SubmitReqState } from "../actions/creators";
import SubmitPurchaseAndSale from "./SubmitPurchaseAndSale";
import ContactInformation from "./ContactInformation";
import SuccessMsg from "../component/SuccessMsg";

const PSABlueprintModal = ({
  isModalOpen,
  onHideHandlr,
  onUploadDocumentAsThunk,
  onSubmitBlueprintAsThunk,
  listingId,
  submitReqState,
  onSubmitReqState,
  uuid,
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedAdditionalFile, setUploadedAdditionalFile] = useState([]);
  const [isChecked, setIsChecked] = useState(true);

  const [isNextClick, setIsNextClick] = useState(false);
  const [addContactCoop, setAddContactCoop] = useState(false);
  const [addContactAdditional, setAddContactAdditional] = useState(false);

  const [comments, setComments] = useState(null);
  const [cooperatingAgent, setCooperatingAgent] = useState([]);
  const [newCooperatingAgent, setNewCooperatingAgent] = useState([]);
  const [newAdditionalContact, setNewAdditionalContact] = useState([]);
  const [additionalContact, setAdditionalContact] = useState([]);

  const onNextClick = () => {
    setIsNextClick(true);
  };

  const onBackClick = () => {
    setIsNextClick(false);
  };

  const onSubmit = async () => {
    onSubmitReqState({ isloading: true, isSucceed: false, isFailed: false, failureMsg: "" });

    const dispatchPromises = [];
    // upload P&S doc
    if (uploadedFile) {
      const data = {
        uploadKey: `upload-0`,
        fileName: uploadedFile.name,
        isUploading: true,
        uploadErrors: [],
        renameErrors: [],
      };
      const formData = new FormData();
      formData.append("upload", uploadedFile);
      formData.append("cda_form", false);
      formData.append("document_type", "blueprint_title");
      formData.append("description", !isChecked ? "Hide from Transaction" : "");
      dispatchPromises.push(onUploadDocumentAsThunk(data, formData, false));
    }
    // upload additional doc
    if (uploadedAdditionalFile.length > 0) {
      uploadedAdditionalFile.forEach((file, i) => {
        const data = {
          uploadKey: `upload-${i + 1}`, // avoid duplicate keys
          fileName: file.name,
          isUploading: true,
          uploadErrors: [],
          renameErrors: [],
        };
        const formData = new FormData();
        formData.append("upload", file);
        formData.append("cda_form", false);
        formData.append("document_type", "blueprint_title");
        formData.append("description", !isChecked ? "Hide from Transaction" : "");
        dispatchPromises.push(onUploadDocumentAsThunk(data, formData, false));
      });
    }

    // dispatchPromises should have at least one item because uploading the P&S document is required
    if (dispatchPromises.length > 0) {
      // Wait for all document upload operations to complete
      const results = await Promise.all(dispatchPromises);
      // Check for errors and collect IDs
      const documentIds = [];
      let hasError = false;
      results.forEach((result) => {
        if (result.errors) {
          hasError = true;
        } else if (result.data?.document_id) {
          documentIds.push(result.data.document_id);
        }
      });
      // Check for errors
      if (hasError) {
        onSubmitReqState({
          isloading: false,
          isSucceed: false,
          isFailed: true,
          failureMsg:
            "There was an issue with submitting your form to Blueprint. Please reach out to support at (855) 427-4848 or start a new chat",
        });
        return; // stop here if there's an error
      }

      // Then, create the blueprint title with documentIds
      const bluePrintData = {
        comment: comments,
        document_ids: documentIds, // get it from result
        cooperating_agent_ids: cooperatingAgent.map((item) => item.value) || [],
        additional_contact_ids: additionalContact.map((item) => item.value) || [],
        new_cooperating_agents: mapToContactPayload(newCooperatingAgent) || [],
        new_additional_contacts: mapToContactPayload(newAdditionalContact) || [],
      };
      await onSubmitBlueprintAsThunk(listingId, uuid, bluePrintData);
    }
  };
  return (
    <Modal
      show={isModalOpen}
      onHide={onHideHandlr}
      dialogClassName="tw-max-w-[638px]"
      contentClassName="tw-w-full tw-h-full tw-px-[22px]"
      className="tw-flex tw-justify-center tw-items-center"
      closeButton
    >
      <Modal.Header className="tw-px-[10px]" title="Open Title with Blueprint" closeButton />

      <Modal.Body className="tw-mb-32px tw-max-h-[70vh] tw-overflow-auto tw-px-[10px]">
        {!submitReqState?.isloading && !submitReqState?.isSucceed && !submitReqState?.isFailed && (
          <>
            {!isNextClick ? (
              <SubmitPurchaseAndSale
                uploadedFile={uploadedFile}
                setUploadedFile={setUploadedFile}
                uploadedAdditionalFile={uploadedAdditionalFile}
                setUploadedAdditionalFile={setUploadedAdditionalFile}
                setIsChecked={setIsChecked}
                isChecked={isChecked}
              />
            ) : (
              <ContactInformation
                setAddContactAdditional={setAddContactAdditional}
                setAddContactCoop={setAddContactCoop}
                addContactCoop={addContactCoop}
                addContactAdditional={addContactAdditional}
                setComments={setComments}
                comments={comments}
                cooperatingAgent={cooperatingAgent}
                setCooperatingAgent={setCooperatingAgent}
                newCooperatingAgent={newCooperatingAgent}
                setNewCooperatingAgent={setNewCooperatingAgent}
                newAdditionalContact={newAdditionalContact}
                setNewAdditionalContact={setNewAdditionalContact}
                additionalContact={additionalContact}
                setAdditionalContact={setAdditionalContact}
              />
            )}
          </>
        )}
        {submitReqState?.isloading && (
          <>
            <div className="tw-border-solid tw-bg-[#F7F9FC] tw-border-neutral-gray-10 tw-h-[216px] tw-w-[552px] tw-mt-[24px] !tw-p-[11px] tw-border tw-rounded-[12px]">
              <Spinner size="xxl" className="tw-animate-spin tw-mx-auto tw-block tw-relative tw-top-1/2" />
            </div>
          </>
        )}
        {submitReqState?.isSucceed && <SuccessMsg />}
        {submitReqState?.isFailed && (
          <Alert
            containerClass="!tw-mt-[24px] !tw-p-[36px] !tw-text-center"
            variant="error"
            text={
              submitReqState?.failureMsg ||
              "There was an issue with submitting your form to Blueprint. Please reach out to support at (855) 427-4848 or start a new chat"
            }
          />
        )}
      </Modal.Body>
      <Modal.Footer
        className={`tw-px-[10px] tw-flex ${submitReqState?.isSucceed || submitReqState?.isFailed ? "tw-justify-center" : "tw-justify-between"} `}
      >
        {!submitReqState?.isloading && !submitReqState?.isSucceed && !submitReqState?.isFailed && (
          <>
            <Button size="medium" schema="tertiary" onClick={!isNextClick ? onHideHandlr : onBackClick}>
              {!isNextClick ? "Cancel" : "Back"}
            </Button>
            <Button
              size="medium"
              onClick={!isNextClick ? onNextClick : onSubmit}
              disabled={!isNextClick ? !uploadedFile : addContactAdditional || addContactCoop}
            >
              {!isNextClick ? "Next" : "Submit"}
            </Button>
          </>
        )}
        {(submitReqState?.isSucceed || submitReqState?.isFailed) && (
          <Button size="medium" onClick={onHideHandlr}>
            Got it!
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
PSABlueprintModal.propTypes = {
  isModalOpen: bool,
  onHideHandlr: func,
  onUploadDocumentAsThunk: func.isRequired,
  onSubmitBlueprintAsThunk: func.isRequired,
  onSubmitReqState: func,
  listingId: number.isRequired,
  uuid: string.isRequired,
  onShowBlueprintFailure: func,
  submitReqState: shape({ isloading: bool, isSucceed: bool, isFailed: bool, failureMsg: string }),
};

PSABlueprintModal.defaultProps = {
  isModalOpen: false,
  onShowBlueprintFailure: null,
  onSubmitReqState: null,
  onHideHandlr: () => {},
  submitReqState: { isloading: false, isSucceed: false, isFailed: false, failureMsg: "" },
};
const mapStateToProps = (state) => ({
  submitReqState: state.tdpTitleAndEscrowReducer.submitReqState,
  listingId: state.tdpDetailsReducer.headerDetails.listing_id,
  uuid: state.tdpDetailsReducer.uuid,
});

const mapDispatchToProps = (dispatch) => ({
  onUploadDocumentAsThunk: (data, formData, isFinancialsCDA) =>
    dispatch(uploadDocumentAsThunk(data, formData, isFinancialsCDA)),
  onSubmitBlueprintAsThunk: (listingId, uuid, data) =>
    dispatch(submitBlueprintAsThunk(listingId, uuid, data)),
  onSubmitReqState: (data) => dispatch(SubmitReqState(data)),
});
export default connect(mapStateToProps, mapDispatchToProps)(PSABlueprintModal);
